import React from 'react';
import { cn } from '../../utils/cn';
import Tooltip from '../ui/Tooltip';
import { CHIP_RISK } from '../../utils/chipColors';

/**
 * Amber "capped" chip surfaced on the SL / Entry trigger cell of:
 *   - StrategyPositionsModal (Classic) via LegSection's `capped` prop
 *   - StrategyPositionsModalV2 (Pro) via PriceCol
 *   - PositionTableView (desktop + mobile cards)
 *
 * Renders only when both `placed` and `desired` are non-null AND the parent
 * gates on `slCircuitCapped` / `entryCircuitCapped`. Tooltip shows both
 * values + the recovery promise.
 *
 * Used inside Tooltip portal so the bubble escapes table/card overflow clips.
 *
 * Keep the underlying span's data-testid stable for tests: callers pass a
 * unique `testId` (e.g. "capped-sl", "capped-entry-mobile") so multiple
 * chips on the same row are individually addressable.
 */
export interface CappedChipProps {
  /** What the broker actually placed (the capped value). */
  placed: number | null | undefined;
  /** What the user configured (the canonical target). */
  desired: number | null | undefined;
  /** "Stop-Loss" | "Entry" — used in the tooltip wording. */
  kind: 'Stop-Loss' | 'Entry';
  /** data-testid passed through to the chip span. */
  testId?: string;
  /** Override class name on the wrapper Tooltip. */
  className?: string;
}

const CappedChip: React.FC<CappedChipProps> = ({
  placed,
  desired,
  kind,
  testId,
  className,
}) => {
  if (placed == null || desired == null) return null;

  const tooltip = `Broker circuit limit clamped this ${kind.toLowerCase()} to ${placed.toFixed(2)} instead of your configured ${desired.toFixed(2)}. Recovery will retry the canonical level as bands widen across days.`;

  return (
    <Tooltip text={tooltip} portal className={className}>
      <span
        className={cn(
          'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider',
          CHIP_RISK.CAPPED,
        )}
        data-testid={testId ?? `capped-${kind.toLowerCase().replace(/\s+/g, '-')}`}
      >
        capped
      </span>
    </Tooltip>
  );
};

export default CappedChip;
